import { getUser } from '../ui/views.js';
import { getKindeInstance } from '../auth/kindeClient.js';

const SERVER_URL = '';

let presenceInterval = null;
let isOnlineStatusActive = false;

/**
 * Starts sending periodic online presence updates to the server
 */
export function startOnlinePresence() {
    if (isOnlineStatusActive) {
        return; // Already active
    }

    const user = getUser();
    if (!user || !user.username) {
        console.warn('Cannot start online presence: User not logged in');
        return;
    }

    isOnlineStatusActive = true;
    console.log('Starting online presence tracking for', user.username);

    // Send initial presence update
    updatePresence();

    // Send presence updates every 30 seconds
    presenceInterval = setInterval(() => {
        updatePresence();
    }, 30000);

    // Send offline status when user leaves
    window.addEventListener('beforeunload', stopOnlinePresence);
    
    // Send offline status when page loses focus for extended periods
    let visibilityTimer = null;
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            // Page hidden - start timer to mark offline after 2 minutes
            visibilityTimer = setTimeout(() => {
                sendOfflineStatus();
            }, 120000); // 2 minutes
        } else {
            // Page visible again - cancel offline timer and send online status
            if (visibilityTimer) {
                clearTimeout(visibilityTimer);
                visibilityTimer = null;
            }
            updatePresence();
        }
    });
}

/**
 * Stops sending online presence updates
 */
export function stopOnlinePresence() {
    if (!isOnlineStatusActive) {
        return;
    }

    console.log('Stopping online presence tracking');
    isOnlineStatusActive = false;

    if (presenceInterval) {
        clearInterval(presenceInterval);
        presenceInterval = null;
    }

    // Send final offline status
    sendOfflineStatus();
}

/**
 * Sends an online presence update to the server
 */
async function updatePresence() {
    const user = getUser();
    if (!user || !user.username) {
        return;
    }

    try {
        const kinde = getKindeInstance();
        const token = await kinde.getToken();
        
        await fetch(`${SERVER_URL}/api/presence/online`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                username: user.username,
                timestamp: Date.now()
            })
        });
    } catch (error) {
        console.error('Error updating online presence:', error);
    }
}

/**
 * Sends offline status to the server
 */
async function sendOfflineStatus() {
    const user = getUser();
    if (!user || !user.username) {
        return;
    }

    try {
        const kinde = getKindeInstance();
        const token = await kinde.getToken();
        
        // Use sendBeacon for reliability on page unload
        const data = JSON.stringify({
            username: user.username,
            timestamp: Date.now()
        });

        if (navigator.sendBeacon) {
            const blob = new Blob([data], { type: 'application/json' });
            navigator.sendBeacon(`${SERVER_URL}/api/presence/offline`, blob);
        } else {
            // Fallback for browsers without sendBeacon
            await fetch(`${SERVER_URL}/api/presence/offline`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: data
            });
        }
    } catch (error) {
        console.error('Error sending offline status:', error);
    }
}

/**
 * Fetches the list of currently online users
 */
export async function getOnlineUsers() {
    try {
        const kinde = getKindeInstance();
        const token = await kinde.getToken();
        
        const response = await fetch(`${SERVER_URL}/api/presence/online-users`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch online users');
        }

        const onlineUsers = await response.json();
        return onlineUsers;
    } catch (error) {
        console.error('Error fetching online users:', error);
        return [];
    }
} 